import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ChevronDown, Bus, X, FileSpreadsheet, Plus } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Material Planning - combine CRR from multiple projects
function MaterialPlanning() {
  const [projects, setProjects] = useState([]);
  const [selections, setSelections] = useState([]);
  const [crrsByProject, setCrrsByProject] = useState({}); // { project_id: [{nfi_code,label,bill_number}] }
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [expandedItem, setExpandedItem] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/planning/projects`);
        const setBased = (res.data || []).filter(p => p.category === 'City Bus' || p.category === 'Coach');
        setProjects(setBased);
      } catch { }
    })();
  }, []);

  const fetchCrrs = async (projectId) => {
    if (!projectId || crrsByProject[projectId]) return;
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/crrs`);
      setCrrsByProject(prev => ({ ...prev, [projectId]: res.data?.crrs || [] }));
    } catch { /* silent */ }
  };

  const addProject = () => setSelections(prev => [...prev, { project_id: '', nfi_code: '', set_qty: 1 }]);
  const removeProject = (idx) => setSelections(prev => prev.filter((_, i) => i !== idx));
  const updateSelection = (idx, field, value) => setSelections(prev => prev.map((s, i) => {
    if (i !== idx) return s;
    const next = { ...s, [field]: value };
    if (field === 'project_id') next.nfi_code = ''; // reset variant when project changes
    return next;
  }));

  const handleCalculate = async () => {
    const valid = selections
      .filter(s => s.project_id && s.set_qty > 0)
      .map(s => ({ project_id: s.project_id, set_qty: s.set_qty, nfi_code: s.nfi_code || '' }));
    if (!valid.length) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/planning/combined-crr`, { selections: valid });
      setResult(res.data);
      setFilter('all'); setGroupFilter('all'); setVendorFilter('all');
    } catch { toast.error('Calculation failed'); }
    setLoading(false);
  };

  const handleExport = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const res = await axios.post(`${API}/api/planning/combined-crr/export`, result, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Combined_CRR_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const s = result?.summary || {};
  const items = result?.items || [];
  const groups = Object.entries(result?.group_summary || {}).sort((a, b) => b[1].short - a[1].short);
  const vendors = Object.entries(result?.vendor_summary || {}).sort((a, b) => b[1].total - a[1].total);

  let filtered = items;
  if (filter === 'short') filtered = items.filter(i => i.total_short > 0);
  else if (filter === 'available') filtered = items.filter(i => i.total_stock >= i.total_required && i.total_required > 0);
  else if (filter === 'no-stock') filtered = items.filter(i => i.total_stock === 0 && i.total_required > 0);
  if (groupFilter !== 'all') filtered = filtered.filter(i => (i.group || 'Unassigned') === groupFilter);
  if (vendorFilter !== 'all') filtered = filtered.filter(i => (i.vendor || 'Unassigned') === vendorFilter);
  if (search) { const q = search.toLowerCase(); filtered = filtered.filter(i => (i.item_code||'').toLowerCase().includes(q)||(i.description||'').toLowerCase().includes(q)||(i.vendor||'').toLowerCase().includes(q)||(i.group||'').toLowerCase().includes(q)); }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Select Projects, Variants & Set Quantities</h3>
            <p className="text-[10px] text-slate-400">Add the same SR multiple times with different variant BOMs (SKD / CBU / Hybrid …) to combine demand accurately.</p>
          </div>
          <button onClick={addProject} className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600 text-white rounded-lg text-xs font-semibold hover:bg-cyan-700" data-testid="add-project-btn">
            <Plus className="w-3.5 h-3.5" /> Add Project
          </button>
        </div>
        {selections.length === 0 && (
          <div className="text-center py-6 text-slate-400 text-sm">
            Click &quot;<span className="font-semibold text-cyan-600">+ Add Project</span>&quot; to start
          </div>
        )}
        {selections.map((sel, idx) => {
          const projectCrrs = crrsByProject[sel.project_id] || [];
          const hasVariants = projectCrrs.length > 1;
          return (
            <div key={idx} className="flex items-center gap-2 mb-1.5" data-testid={`selection-row-${idx}`}>
              <span className="w-5 text-[10px] font-bold text-slate-400 text-right">{idx + 1}.</span>
              <select
                value={sel.project_id}
                onChange={e => { updateSelection(idx, 'project_id', e.target.value); fetchCrrs(e.target.value); }}
                className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:ring-cyan-400"
                data-testid={`selection-project-${idx}`}
              >
                <option value="">-- Select Project --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.category === 'Coach' ? '[Coach] ' : ''}{p.name} ({p.sr_number || '-'}) - {p.ordered_quantity || 0} ordered
                  </option>
                ))}
              </select>
              <select
                value={sel.nfi_code || ''}
                onChange={e => updateSelection(idx, 'nfi_code', e.target.value)}
                disabled={!sel.project_id}
                className={`w-40 border rounded-lg px-2 py-1.5 text-xs focus:ring-indigo-400 ${hasVariants ? 'border-indigo-300 bg-indigo-50 font-semibold' : 'border-slate-200'} disabled:opacity-40`}
                data-testid={`selection-variant-${idx}`}
                title={hasVariants ? 'Multiple BOMs exist — pick one' : 'Only primary BOM available'}
              >
                {projectCrrs.length === 0 && <option value="">Primary</option>}
                {projectCrrs.map(c => (
                  <option key={c.nfi_code || 'primary'} value={c.nfi_code || ''}>
                    {c.is_primary ? '★ Primary' : (c.label || c.nfi_code || c.bill_number)}
                    {c.nfi_code ? ` · ${c.nfi_code}` : ''}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-semibold text-slate-500">Sets:</span>
                <input
                  type="number" min={1} value={sel.set_qty}
                  onChange={e => updateSelection(idx, 'set_qty', parseInt(e.target.value) || 1)}
                  className="w-14 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-center font-bold focus:ring-cyan-400"
                  data-testid={`selection-qty-${idx}`}
                />
              </div>
              <button
                onClick={() => removeProject(idx)}
                className="p-1 text-red-400 hover:text-red-600"
                data-testid={`selection-remove-${idx}`}
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
        {selections.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              onClick={addProject}
              className="flex items-center gap-1 px-3 py-1.5 border border-dashed border-cyan-300 text-cyan-600 rounded-lg text-xs font-semibold hover:bg-cyan-50"
              data-testid="add-project-btn-bottom"
            >
              <Plus className="w-3.5 h-3.5" /> Add another
            </button>
            <button
              onClick={handleCalculate}
              disabled={loading || !selections.some(s => s.project_id)}
              className="px-8 py-2.5 bg-cyan-600 text-white rounded-xl text-sm font-bold hover:bg-cyan-700 disabled:opacity-40 shadow-lg shadow-cyan-200"
              data-testid="calculate-btn"
            >
              {loading ? 'Calculating...' : 'Calculate Combined Requirements'}
            </button>
            <span className="w-24" />
          </div>
        )}
      </div>

      {result && (
        <div className="bg-white border border-cyan-200 rounded-xl overflow-hidden">
          <div className="bg-cyan-50 px-4 py-2.5 border-b border-cyan-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-cyan-600" />
              <span className="text-xs font-bold text-cyan-800">Combined Material Requirements</span>
              <span className="text-[10px] text-cyan-500">{result.total_items} unique items</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap gap-1 text-[9px]">
                {result.projects?.map(p => (
                  <span key={p.id} className="bg-white border border-cyan-200 px-1.5 py-0.5 rounded font-semibold text-cyan-700">{p.name}: {p.set_qty} set</span>
                ))}
              </div>
              <button onClick={handleExport} disabled={exporting}
                className="flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white rounded text-[10px] font-semibold hover:bg-emerald-700 disabled:opacity-50">
                {exporting ? '...' : 'Export Excel'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 p-3">
            {[
              { key: 'all', label: 'Total', value: s.total, color: 'slate' },
              { key: 'available', label: 'Available', value: s.available, color: 'emerald' },
              { key: 'short', label: 'Short', value: s.short, color: 'red' },
              { key: 'no-stock', label: 'No Stock', value: s.no_stock, color: 'orange' },
            ].map(c => (
              <button key={c.key} onClick={() => setFilter(c.key)}
                className={`text-center p-2.5 rounded-lg border transition-all ${filter === c.key ? `bg-${c.color}-50 border-${c.color}-300 ring-1 ring-${c.color}-200` : 'bg-white border-slate-200'}`}>
                <div className={`text-xl font-black text-${c.color}-600`}>{c.value || 0}</div>
                <div className={`text-[9px] text-${c.color}-500 font-semibold uppercase`}>{c.label}</div>
              </button>
            ))}
          </div>

          <div className="px-3 pb-2 flex items-center gap-2">
            <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] font-semibold focus:ring-cyan-400 focus:outline-none min-w-[140px]">
              <option value="all">All Groups ({items.length})</option>
              {groups.map(([g, gs]) => (
                <option key={g} value={g}>{g}: {gs.total}{gs.short > 0 ? ` (${gs.short} short)` : ''}</option>
              ))}
            </select>
            <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] font-semibold focus:ring-violet-400 focus:outline-none min-w-[160px]">
              <option value="all">All Vendors</option>
              {vendors.map(([v, vs]) => (
                <option key={v} value={v}>{v}: {vs.total}{vs.short > 0 ? ` (${vs.short} short)` : ''}</option>
              ))}
            </select>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, description, vendor..."
              className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:ring-cyan-400 focus:outline-none focus:border-cyan-300" data-testid="mp-search" />
            {(groupFilter !== 'all' || vendorFilter !== 'all' || search) && (
              <button onClick={() => { setGroupFilter('all'); setVendorFilter('all'); setSearch(''); }}
                className="text-[10px] font-semibold text-slate-400 hover:text-red-500 px-2">Clear</button>
            )}
            <span className="text-[10px] text-slate-400 whitespace-nowrap">{filtered.length} items</span>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-slate-400 uppercase tracking-wider text-[8px]">
                  <th className="px-2 py-1.5 text-left font-semibold">#</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Item Code</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Description</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Group</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Vendor</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Total Req</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-indigo-500">KSU</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-orange-500">Kiel</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Total</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-red-400">Short</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => {
                  const isShort = item.total_short > 0;
                  const isExpanded = expandedItem === item.item_code;
                  return (
                    <React.Fragment key={item.item_code}>
                      <tr className={`${isShort ? 'bg-red-50/50' : i % 2 ? 'bg-slate-50/30' : ''} hover:bg-blue-50/30 cursor-pointer`}
                        onClick={() => setExpandedItem(isExpanded ? null : item.item_code)}>
                        <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                        <td className="px-2 py-1.5 font-mono font-semibold text-slate-700">
                          <span className="flex items-center gap-1">
                            <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            {item.item_code}
                            {item.project_count > 1 && <span className="text-[8px] bg-cyan-100 text-cyan-700 px-1 rounded">{item.project_count}p</span>}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-slate-600 max-w-[180px] truncate">{item.description}</td>
                        <td className="px-2 py-1.5 text-slate-500">{item.group || '-'}</td>
                        <td className="px-2 py-1.5 text-slate-500 max-w-[120px] truncate">{item.vendor || '-'}</td>
                        <td className="px-2 py-1.5 text-right text-blue-600 font-bold">{item.total_required}</td>
                        <td className="px-2 py-1.5 text-right text-indigo-600">{item.ksu_available || 0}</td>
                        <td className="px-2 py-1.5 text-right text-orange-600">{item.kiel_available || 0}</td>
                        <td className="px-2 py-1.5 text-right font-bold text-slate-700">{item.total_stock || 0}</td>
                        <td className={`px-2 py-1.5 text-right font-bold ${isShort ? 'text-red-600' : 'text-slate-300'}`}>{item.total_short || 0}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-50 border-l-4 border-cyan-400">
                          <td colSpan={10} className="px-4 py-3">
                            <div className="grid grid-cols-3 gap-4">
                              <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
                                <div className="text-[10px] font-bold text-cyan-700 uppercase mb-2">Per Project Breakdown</div>
                                {item.project_breakdown?.map((pb, pi) => (
                                  <div key={pi} className="flex items-center justify-between text-[10px] mb-1">
                                    <span className="text-slate-600">
                                      {pb.project}
                                      {pb.nfi_label && (
                                        <span className="ml-1.5 text-[9px] font-semibold text-indigo-600 bg-indigo-100 px-1 py-0.5 rounded" title={pb.nfi_code}>
                                          {pb.nfi_label}
                                        </span>
                                      )}
                                    </span>
                                    <span className="font-mono font-semibold">{pb.per_bill} x {pb.set_qty} = <span className="text-blue-600">{pb.required}</span></span>
                                  </div>
                                ))}
                              </div>
                              <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                                <div className="text-[10px] font-bold text-indigo-700 uppercase mb-2">KSU Detail</div>
                                <div className="grid grid-cols-4 gap-2 text-center">
                                  <div><div className="text-[9px] text-slate-400">On Hand</div><div className="text-sm font-bold text-indigo-700">{item.ksu_on_hand || 0}</div></div>
                                  <div><div className="text-[9px] text-slate-400">On PO</div><div className={`text-sm font-bold ${item.ksu_on_po > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{item.ksu_on_po || 0}</div></div>
                                  <div><div className="text-[9px] text-slate-400">On SO</div><div className={`text-sm font-bold ${item.ksu_on_so > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{item.ksu_on_so || 0}</div></div>
                                  <div><div className="text-[9px] text-slate-400">Available</div><div className="text-sm font-bold text-indigo-600">{item.ksu_available || 0}</div></div>
                                </div>
                              </div>
                              <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                                <div className="text-[10px] font-bold text-orange-700 uppercase mb-2">Kiel Detail</div>
                                <div className="grid grid-cols-4 gap-2 text-center">
                                  <div><div className="text-[9px] text-slate-400">On Hand</div><div className="text-sm font-bold text-orange-700">{item.kiel_on_hand || 0}</div></div>
                                  <div><div className="text-[9px] text-slate-400">On PO</div><div className={`text-sm font-bold ${item.kiel_on_po > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{item.kiel_on_po || 0}</div></div>
                                  <div><div className="text-[9px] text-slate-400">On SO</div><div className={`text-sm font-bold ${item.kiel_on_so > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{item.kiel_on_so || 0}</div></div>
                                  <div><div className="text-[9px] text-slate-400">Available</div><div className="text-sm font-bold text-orange-600">{item.kiel_available || 0}</div></div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterialPlanning;
